import React from 'react';

/**
 * The one field vocabulary the Explore forms share (contact, partner,
 * application). Inputs sit on the canvas as hairline-underlined lines rather
 * than boxed cards, which is rule 1 of the Inception line applied to a form.
 *
 * Plain constants and presentational components only: no hooks, no state, so
 * the three client forms can import it without this file claiming a boundary of
 * its own.
 *
 * Font size: `text-base` is 15px at the root scale, but globals.css forces 16px
 * on every input and textarea below 768px, which is what keeps iOS from zooming.
 */

export const exploreLabelClass = 'mb-1.5 block text-sm font-medium text-ink';

export const exploreFieldClass =
  'w-full rounded-none border-0 border-b border-hairline bg-transparent px-0 py-2.5 text-base font-light text-ink placeholder-muted/50 transition-colors duration-200 focus:border-accent focus:outline-none focus:ring-0';

export const exploreTextAreaClass = `${exploreFieldClass} resize-none`;

/** Muted asterisk. The accent is reserved for hover and focus. */
export const RequiredMark = () => (
  <span aria-hidden className="mr-1 text-xs text-muted">
    *
  </span>
);

export const ExploreField = ({
  id,
  label,
  required,
  helper,
  children,
  className = '',
}: {
  id: string;
  label: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <label htmlFor={id} className={exploreLabelClass}>
      {required ? <RequiredMark /> : null}
      {label}
    </label>
    {children}
    {helper ? <p className="mt-1.5 text-xs font-light text-muted">{helper}</p> : null}
  </div>
);

/** Section opener inside a long form: a hairline, a kicker, a serif title. */
export const ExploreFormSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <fieldset className="border-t border-hairline pt-8">
    <legend className="sr-only">{title}</legend>
    <p aria-hidden className="font-display text-xl font-normal text-ink sm:text-2xl">
      {title}
    </p>
    <div className="mt-6 space-y-6">{children}</div>
  </fieldset>
);

/**
 * The Explore submit: an ink pill that turns accent on hover, so the page keeps
 * exactly one saturated colour and it is a state, not a decoration.
 */
export const ExploreSubmit = ({
  label,
  loadingLabel,
  loading,
}: {
  label: string;
  loadingLabel: string;
  loading: boolean;
}) => (
  <button
    type="submit"
    disabled={loading}
    className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-card transition-colors duration-200 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
  >
    {loading ? (
      <>
        <span
          aria-hidden
          className="h-4 w-4 animate-spin rounded-full border-2 border-card/30 border-t-card motion-reduce:animate-none"
        />
        <span>{loadingLabel}</span>
      </>
    ) : (
      <span>{label}</span>
    )}
  </button>
);

/** What a sent form leaves behind: a serif line, a muted line, nothing else. */
export const ExploreFormSuccess = ({ title, body }: { title: string; body: string }) => (
  <div className="py-8">
    <p className="font-display text-3xl font-normal leading-tight text-ink sm:text-4xl">{title}</p>
    <p className="mt-4 max-w-[48ch] text-base font-light leading-relaxed text-muted">{body}</p>
  </div>
);

export const ExploreFormError = ({ message }: { message: string }) => (
  <p role="alert" className="text-sm font-light text-danger">
    {message}
  </p>
);
